import type { Circuit } from '@multirx/shared/types/track.js';
import { replaceWebGlCanvasWithSnapshot } from '../cars/car3dPreview.js';
import { createCardEarthLoader } from '../ui/earthLoader.js';
import { mountCircuit3dPreview, type Circuit3dPreviewHandle } from './circuit3dPreview.js';

export interface LazyCircuit3dPreviewOptions {
  /**
   * Fige le premier rendu dans une `<img>` puis libère le contexte WebGL.
   * Indispensable pour les grilles (replays, listes) : trop de renderers
   * simultanés saturent la limite navigateur (~8–16) et laissent des cartes
   * blanches / noires « cassées ».
   */
  snapshot?: boolean;
  /** Tentatives auto après un plantage WebGL / capture (défaut 3). */
  maxRetries?: number;
  /** Message sous la planète pendant le chargement snapshot (défaut « Chargement… »). */
  loadingMessage?: string;
  /** Appelé une fois avec le data URL PNG après capture réussie (backfill serveur). */
  onSnapshot?: (dataUrl: string) => void;
}

/** Limite les montages live simultanés (avant snapshot) pour ne pas saturer le GPU. */
const MAX_CONCURRENT_MOUNTS = 2;
let activeMounts = 0;
const mountQueue: Array<() => void> = [];

function scheduleMount(start: () => void): () => void {
  let started = false;
  let cancelled = false;
  const task = (): void => {
    if (cancelled) return;
    started = true;
    activeMounts++;
    start();
  };
  if (activeMounts < MAX_CONCURRENT_MOUNTS) task();
  else mountQueue.push(task);
  return () => {
    if (started) return;
    cancelled = true;
    const i = mountQueue.indexOf(task);
    if (i !== -1) mountQueue.splice(i, 1);
  };
}

function releaseMountSlot(): void {
  activeMounts = Math.max(0, activeMounts - 1);
  const next = mountQueue.shift();
  next?.();
}

function previewWrapOf(container: HTMLElement): HTMLElement | null {
  return container.closest('.circuit-card-preview-wrap');
}

function loaderHost(container: HTMLElement): HTMLElement {
  return previewWrapOf(container) ?? container;
}

function setCardLoading(container: HTMLElement, loading: boolean): void {
  container.classList.toggle('circuit-preview--loading', loading);
  previewWrapOf(container)?.classList.toggle('is-loading', loading);
}

function findCardLoader(host: HTMLElement): HTMLElement | null {
  return host.querySelector('.circuit-preview-earth-loader');
}

function showCardLoader(container: HTMLElement, message: string): void {
  const host = loaderHost(container);
  if (!findCardLoader(host)) {
    host.appendChild(createCardEarthLoader(message));
  }
  setCardLoading(container, true);
}

function hideCardLoader(container: HTMLElement): void {
  findCardLoader(loaderHost(container))?.remove();
  findCardLoader(container)?.remove();
  setCardLoading(container, false);
}

function clearRenderedPreview(container: HTMLElement): void {
  container.querySelector('canvas')?.remove();
  container.querySelector('.circuit-card-3d-snapshot')?.remove();
  container.querySelector('.circuit-preview-retry')?.remove();
  container.classList.remove('circuit-preview--failed');
}

function showRetryButton(container: HTMLElement, onRetry: () => void): void {
  hideCardLoader(container);
  container.classList.add('circuit-preview--failed');
  if (container.querySelector('.circuit-preview-retry')) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'circuit-preview-retry';
  btn.textContent = 'Réessayer';
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearRenderedPreview(container);
    onRetry();
  });
  container.appendChild(btn);
}

function hidePendingCanvas(container: HTMLElement): void {
  const canvas = container.querySelector('canvas');
  if (canvas instanceof HTMLCanvasElement) {
    canvas.classList.add('circuit-preview-canvas--pending');
  }
}

/** True si le canvas est quasi noir / vide (snapshot inutilisable). */
export function isCircuitPreviewCanvasBlank(canvas: HTMLCanvasElement): boolean {
  if (canvas.width < 2 || canvas.height < 2) return true;
  try {
    const w = Math.min(canvas.width, 48);
    const h = Math.min(canvas.height, 48);
    const tmp = document.createElement('canvas');
    tmp.width = w;
    tmp.height = h;
    const ctx = tmp.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(canvas, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    let lit = 0;
    const total = w * h;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i]! > 24 || data[i + 1]! > 24 || data[i + 2]! > 24) lit++;
    }
    return lit < total * 0.02;
  } catch {
    return false;
  }
}

/**
 * Monte une vignette 3D dans `container` seulement quand elle devient visible.
 * Avec `snapshot: true`, la planète reste affichée jusqu’à un snapshot valide.
 */
export function mountLazyCircuit3dPreview(
  container: HTMLElement,
  circuit: Circuit | null | undefined,
  root?: Element | null,
  circuitName?: string | null,
  options?: LazyCircuit3dPreviewOptions
): () => void {
  if (!circuit?.path?.length) return () => {};

  const useSnapshot = options?.snapshot === true;
  const maxRetries = Math.max(0, options?.maxRetries ?? 3);
  const loadingMessage = options?.loadingMessage ?? 'Chargement…';

  let handle: Circuit3dPreviewHandle | null = null;
  let cancelQueued: (() => void) | null = null;
  let visible = false;
  let disposed = false;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let snapshotted = false;
  let slotHeld = false;

  if (useSnapshot) showCardLoader(container, loadingMessage);

  const clearRetryTimer = (): void => {
    if (retryTimer != null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const disposeLive = (): void => {
    handle?.dispose();
    handle = null;
  };

  const finishMountSlot = (): void => {
    if (!slotHeld) return;
    slotHeld = false;
    cancelQueued = null;
    releaseMountSlot();
  };

  const captureSnapshot = (): boolean => {
    const canvas = container.querySelector('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) return false;
    if (isCircuitPreviewCanvasBlank(canvas)) return false;
    const dataUrl = replaceWebGlCanvasWithSnapshot(container, canvas);
    if (!dataUrl) return false;
    const img = container.querySelector('img');
    if (img) img.className = 'circuit-card-3d-snapshot';
    disposeLive();
    snapshotted = true;
    hideCardLoader(container);
    try {
      options?.onSnapshot?.(dataUrl);
    } catch {
      /* ignore callback errors */
    }
    return true;
  };

  const scheduleRetry = (): void => {
    if (disposed || !visible) {
      if (useSnapshot && !snapshotted) showCardLoader(container, loadingMessage);
      return;
    }
    if (attempt >= maxRetries) {
      showRetryButton(container, () => {
        attempt = 0;
        snapshotted = false;
        startMount();
      });
      return;
    }
    showCardLoader(container, loadingMessage);
    clearRetryTimer();
    const delay = 350 * Math.max(1, attempt);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (disposed || !visible || snapshotted) return;
      startMount();
    }, delay);
  };

  const startMount = (): void => {
    if (disposed || !visible || handle || snapshotted || cancelQueued) return;
    if (useSnapshot) showCardLoader(container, loadingMessage);
    cancelQueued = scheduleMount(() => {
      cancelQueued = null;
      if (disposed || !visible || snapshotted) {
        releaseMountSlot();
        return;
      }
      slotHeld = true;
      attempt += 1;
      clearRenderedPreview(container);
      if (useSnapshot) showCardLoader(container, loadingMessage);
      try {
        handle = mountCircuit3dPreview(container, circuit, undefined, {
          interactive: false,
          circuitName,
        });
        if (useSnapshot) hidePendingCanvas(container);
      } catch {
        finishMountSlot();
        scheduleRetry();
        return;
      }

      if (!useSnapshot) {
        finishMountSlot();
        return;
      }

      void handle.ready
        .then(() => {
          if (disposed || !visible || !handle) {
            finishMountSlot();
            return;
          }
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (disposed || !visible || !handle) {
                finishMountSlot();
                return;
              }
              const ok = captureSnapshot();
              finishMountSlot();
              if (!ok) scheduleRetry();
            });
          });
        })
        .catch(() => {
          disposeLive();
          finishMountSlot();
          scheduleRetry();
        });
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        visible = entry.isIntersecting;
        if (visible) {
          if (!snapshotted) {
            if (useSnapshot) showCardLoader(container, loadingMessage);
            startMount();
          }
        } else {
          cancelQueued?.();
          cancelQueued = null;
          clearRetryTimer();
          if (!snapshotted) {
            disposeLive();
            clearRenderedPreview(container);
            if (useSnapshot) showCardLoader(container, loadingMessage);
          }
        }
      }
    },
    { root: root ?? null, rootMargin: root ? '0px' : '120px' }
  );
  observer.observe(container);

  return () => {
    disposed = true;
    observer.disconnect();
    cancelQueued?.();
    cancelQueued = null;
    clearRetryTimer();
    disposeLive();
    clearRenderedPreview(container);
    hideCardLoader(container);
  };
}
